import { getSynth, Voice } from './synth';
import { TempoMap, unwarp, warp } from './tempoMap';

export interface PlayNote {
  onset: number;
  dur: number;
  midi: number;
  velocity?: number;
  line?: number;
}

export type StopReason = 'end' | 'hold' | 'pause';

type Listener = (position: number, playing: boolean) => void;

const LOOKAHEAD = 0.14;
const TICK_MS = 25;

function sameNotes(a: PlayNote[], b: PlayNote[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x.onset !== y.onset || x.dur !== y.dur || x.midi !== y.midi || (x.velocity ?? 0.75) !== (y.velocity ?? 0.75)) return false;
  }
  return true;
}

/** Only one player sounds at a time: starting one pauses the others. */
const players = new Set<Player>();

/**
 * A look-ahead scheduler that plays note events (in quarter notes) through the shared
 * synth. It supports tempo changes, loops, "hold" points (stop before a resolution)
 * and swapping the note list mid-flight (A/B what-if comparisons) without losing place.
 */
export class Player {
  private notes: PlayNote[] = [];
  private length = 0;
  private bpm = 66;
  /** Speed changes along the music: from `at` (quarters) the tempo is `factor` × bpm. */
  private tempoMap: TempoMap = [];
  private loop: { start: number; end: number } | null = null;
  private holdAt: number | null = null;
  private playing = false;
  private pausedAt = 0;
  private anchors: Array<{ time: number; pos: number }> = [];
  private cursor = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private finishTimer: ReturnType<typeof setTimeout> | null = null;
  private finishing = false;
  private voices: Voice[] = [];
  private listeners = new Set<Listener>();
  private raf = 0;
  onStop: ((reason: StopReason, position: number) => void) | null = null;

  setNotes(notes: PlayNote[], length?: number): void {
    const sorted = [...notes].sort((a, b) => a.onset - b.onset || a.midi - b.midi);
    const newLength = length ?? sorted.reduce((a, n) => Math.max(a, n.onset + n.dur), 0);
    // The same music again (e.g. a caller re-sending on every render) must not restart the sound.
    if (newLength === this.length && sameNotes(sorted, this.notes)) return;
    const wasPlaying = this.playing;
    const pos = this.position;
    this.notes = sorted;
    this.length = newLength;
    if (wasPlaying) this.restartAt(Math.min(pos, this.length), true);
    else this.pausedAt = Math.min(this.pausedAt, this.length);
  }

  get total(): number {
    return this.length;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  get tempo(): number {
    return this.bpm;
  }

  setTempo(bpm: number): void {
    const pos = this.position;
    this.bpm = bpm;
    if (this.playing) this.restartAt(pos, false);
  }

  /** Changes of speed along the music (an Adagio variation). Positions stay in quarter notes. */
  setTempoMap(map: Array<{ at: number; factor: number }> | null | undefined): void {
    const next = (map ?? []).filter((x) => x.factor > 0).sort((a, b) => a.at - b.at);
    if (next.length === this.tempoMap.length && next.every((x, i) => x.at === this.tempoMap[i].at && x.factor === this.tempoMap[i].factor)) return;
    const pos = this.position;
    this.tempoMap = next;
    if (this.playing) this.restartAt(pos, false);
  }

  private warp(pos: number): number {
    return warp(this.tempoMap, pos);
  }

  private unwarp(w: number): number {
    return unwarp(this.tempoMap, w);
  }

  setLoop(range: { start: number; end: number } | null): void {
    this.loop = range && range.end > range.start ? range : null;
    if (this.playing) {
      const pos = this.position;
      const inside = this.loop && pos >= this.loop.start && pos < this.loop.end;
      this.restartAt(this.loop && !inside ? this.loop.start : pos, false);
    }
  }

  /** Stop playback when this position is reached (used by "Hold before home"). */
  setHold(pos: number | null): void {
    this.holdAt = pos;
    if (this.playing) this.restartAt(this.position, false);
  }

  get position(): number {
    if (!this.playing || !this.anchors.length) return this.pausedAt;
    const now = getSynth().now;
    let a = this.anchors[0];
    for (const x of this.anchors) if (x.time <= now) a = x;
    const pos = this.unwarp(this.warp(a.pos) + Math.max(0, now - a.time) * (this.bpm / 60));
    return Math.min(pos, this.segmentEnd());
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.position, this.playing);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    const pos = this.position;
    this.listeners.forEach((fn) => fn(pos, this.playing));
  }

  private segmentEnd(): number {
    if (this.loop) return this.loop.end;
    return Math.min(this.holdAt ?? Infinity, this.length);
  }

  constructor() {
    players.add(this);
  }

  /** Forget this player (call when its view unmounts). */
  dispose(): void {
    this.pause();
    players.delete(this);
  }

  async play(from?: number): Promise<void> {
    // Re-register: in development React mounts effects twice, and the first cleanup disposes the player.
    players.add(this);
    players.forEach((p) => {
      if (p !== this && p.isPlaying) p.pause();
    });
    const synth = getSynth();
    await synth.resume();
    let start = from ?? this.pausedAt;
    if (start >= this.length - 1e-6 || (this.holdAt !== null && start >= this.holdAt - 1e-6 && !this.loop)) start = this.loop?.start ?? 0;
    if (this.loop && (start < this.loop.start || start >= this.loop.end)) start = this.loop.start;
    this.playing = true;
    this.restartAt(start, false);
    this.startFrames();
  }

  pause(): void {
    if (!this.playing) return;
    this.pausedAt = this.position;
    this.halt('pause');
  }

  seek(pos: number): void {
    const p = Math.max(0, Math.min(pos, this.length));
    if (this.playing) this.restartAt(p, true);
    else {
      this.pausedAt = p;
      this.emit();
    }
  }

  stopAll(): void {
    this.pausedAt = 0;
    if (this.playing) this.halt('pause');
    else this.emit();
  }

  private halt(reason: StopReason): void {
    this.playing = false;
    if (this.timer) clearInterval(this.timer);
    if (this.finishTimer) clearTimeout(this.finishTimer);
    this.timer = null;
    this.finishTimer = null;
    this.finishing = false;
    const now = getSynth().now;
    this.voices.forEach((v) => v.stop(now));
    this.voices = [];
    cancelAnimationFrame(this.raf);
    this.emit();
    this.onStop?.(reason, this.pausedAt);
  }

  private restartAt(pos: number, retrigger: boolean): void {
    const synth = getSynth();
    const now = synth.now;
    this.voices.forEach((v) => v.stop(now + 0.01));
    this.voices = [];
    if (this.finishTimer) clearTimeout(this.finishTimer);
    this.finishTimer = null;
    this.finishing = false;
    const startTime = now + 0.04;
    this.anchors = [{ time: startTime, pos }];
    this.cursor = pos;
    if (retrigger) {
      // Notes already sounding at this position (held bass, pedal) restart softly.
      for (const n of this.notes) {
        if (n.onset < pos - 1e-6 && n.onset + n.dur > pos + 0.05) {
          const remaining = (this.warp(n.onset + n.dur) - this.warp(pos)) * (60 / this.bpm);
          this.voices.push(synth.play(n.midi, startTime, remaining, (n.velocity ?? 0.7) * 0.6));
        }
      }
    }
    if (this.timer) clearInterval(this.timer);
    this.timer = setInterval(() => this.tick(), TICK_MS);
    this.tick();
  }

  private timeOf(pos: number): number {
    const a = this.anchors[this.anchors.length - 1];
    return a.time + (this.warp(pos) - this.warp(a.pos)) * (60 / this.bpm);
  }

  private tick(): void {
    if (!this.playing || this.finishing) return;
    const synth = getSynth();
    const horizon = synth.now + LOOKAHEAD;
    const secPerQ = 60 / this.bpm;
    for (let guard = 0; guard < 4; guard++) {
      const a = this.anchors[this.anchors.length - 1];
      const horizonPos = this.unwarp(this.warp(a.pos) + (horizon - a.time) / secPerQ);
      const segEnd = this.segmentEnd();
      const limit = Math.min(horizonPos, segEnd);
      for (const n of this.notes) {
        if (n.onset < this.cursor - 1e-9) continue;
        if (n.onset >= limit - 1e-9) break;
        const t = this.timeOf(n.onset);
        const dur = this.timeOf(n.onset + Math.min(n.dur, segEnd - n.onset + 0.5)) - t;
        this.voices.push(synth.play(n.midi, t, dur, n.velocity ?? 0.75));
      }
      this.cursor = limit;
      if (horizonPos < segEnd) break;
      if (this.loop) {
        const wrapTime = this.timeOf(this.loop.end);
        this.anchors.push({ time: wrapTime, pos: this.loop.start });
        if (this.anchors.length > 8) this.anchors.splice(0, this.anchors.length - 8);
        this.cursor = this.loop.start;
        continue;
      }
      const endTime = this.timeOf(segEnd);
      const reason: StopReason = this.holdAt !== null && segEnd >= this.holdAt - 1e-9 && segEnd < this.length ? 'hold' : 'end';
      this.finishing = true;
      this.finishTimer = setTimeout(() => {
        this.pausedAt = reason === 'end' ? 0 : segEnd;
        this.playing = false;
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
        this.finishing = false;
        cancelAnimationFrame(this.raf);
        this.emit();
        this.onStop?.(reason, segEnd);
      }, Math.max(0, (endTime - synth.now) * 1000));
      break;
    }
    const now = synth.now;
    this.voices = this.voices.filter((v) => v.startTime > now - 30);
  }

  private startFrames(): void {
    cancelAnimationFrame(this.raf);
    const frame = () => {
      this.emit();
      if (this.playing) this.raf = requestAnimationFrame(frame);
    };
    this.raf = requestAnimationFrame(frame);
  }
}
